// src/cmps/HomePage/AnimeScheduleV2.jsx
// Toggle-button + SEO/a11y, מחובר ל-watchlist.slice (Redux-Toolkit החדש)

import { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'

/* thunks מה-slice החדש */
import {
  loadList, // מביא רשימה קומפקטית + טוען relations
  loadStatusMap, // id → {status,progress}
  addItem as addItemThunk,
  removeItem as removeItemThunk,
} from '../../store/slices/watchlist.slice'

import { getCurrentDay } from '../../services/schedule.service'
import { getAnimeDetailsUrl, utilService } from '../../services/util.service'

import {
  animeSchedule,
  images,
  topAnimesImages,
} from '../../services/images.service'
import { fetchAnimeScheduleByDay } from '../../store/slices/animeFeatures.slice'
import clsx from 'clsx'
import { FireDot, WatchlistIcon } from '../ui/icons/shared'

/* ——————————————————— ימים ——————————————————— */
const filterDays = [
  { id: 'sunday', label: 'ראשון' },
  { id: 'monday', label: 'שני' },
  { id: 'tuesday', label: 'שלישי' },
  { id: 'wednesday', label: 'רביעי' },
  { id: 'thursday', label: 'חמישי' },
  { id: 'friday', label: 'שישי' },
  { id: 'saturday', label: 'שבת' },
]

/* ——————————————————— קומפוננטה ——————————————————— */
export function AnimeScheduleV2() {
  const dispatch = useDispatch()
  const location = useLocation()

  const [selectedDay, setSelectedDay] = useState(getCurrentDay())
  const [currentPage, setCurrentPage] = useState(0)

  /* Redux */
  const loggedInUser = useSelector((s) => s.userModule.loggedInUser)

  /* statusMap[id] -> {status,progress} */
  const statusMap = useSelector(
    (s) => s.watchlist.statusMap?.[loggedInUser?._id] || {}
  )

  /* fullWatchlist לאייקון fallback אם map עדיין לא נטען */
  const watchlistAll = useSelector((s) =>
    Object.values(s.watchlist.watchlist[loggedInUser?._id]?.all || {}).flat()
  )

  const schedules = useSelector((s) => s.animeFeatures.schedules)
  const loading = useSelector((s) => s.animeFeatures.loading.schedule)

  const currentItems = useMemo(
    () => schedules[selectedDay] || [],
    [schedules, selectedDay]
  )

  /* ——— side-effects ——— */
  useEffect(() => {
    /* לוח שידורים */
    if (!schedules[selectedDay]) dispatch(fetchAnimeScheduleByDay(selectedDay))

    /* watchlist קומפקטי + statusMap */
    if (loggedInUser) {
      /* קומפקטי */
      dispatch(
        loadList({
          userId: loggedInUser._id,
          listType: 'watchlist',
          cacheKey: 'schedule',
          options: { limit: 150, fields: 'anizone_id,status' },
        })
      )
      /* statusMap רק לסדרות של אותו יום */
      dispatch(
        loadStatusMap({
          userId: loggedInUser._id,
          listType: 'watchlist',
          anizoneIds: currentItems.map((i) => i.mal_id),
        })
      )
    }
  }, [dispatch, loggedInUser?._id, selectedDay, currentItems, schedules])

  /* Pagination helpers */
  const ITEMS_PER_PAGE = { mobile: 6, tablet: 10, desktop: 12 }
  const getItemsPerPage = () => {
    if (window.matchMedia('(min-width: 1224px)').matches)
      return ITEMS_PER_PAGE.desktop
    if (window.matchMedia('(min-width: 768px)').matches)
      return ITEMS_PER_PAGE.tablet
    return ITEMS_PER_PAGE.mobile
  }
  const itemsPerPage = getItemsPerPage()
  const paginatedItems = currentItems.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  )

  /* helpers */
  const isInWatch = (id) => Boolean(statusMap[id])
  const isInWatchIcon = (id) =>
    isInWatch(id) || watchlistAll.some((i) => i.anizone_id === id)

  const handleToggleWatch = (anime) => {
    if (!loggedInUser) return toast.error('התחבר כדי לערוך רשימה')

    if (isInWatch(anime.mal_id)) {
      dispatch(
        removeItemThunk({
          userId: loggedInUser._id,
          listType: 'watchlist',
          anizoneId: anime.mal_id,
        })
      )
      toast.success('הוסר מרשימת הצפייה')
    } else {
      utilService.handleAddItem(
        'Watching',
        anime,
        dispatch,
        loggedInUser,
        [], // אין צורך ב-fullWatchlist
        addItemThunk
      )
    }
  }

  /* ——————————————————— Render ——————————————————— */
  return (
    <section className="mt-6">
      {/* כותרת */}
      <div className="flex justify-between items-center">
        <h2 className="text-h2 text-softText">לוח שידורים</h2>
      </div>

      {/* כפתורי יום */}
      <div
        className="relative flex gap-4 mt-4 justify-center"
        role="tablist"
        aria-label="בחירת יום שידור"
      >
        {filterDays.map((day) => (
          <button
            key={day.id}
            type="button"
            className="relative transform transition-transform duration-300 hover:scale-105 focus:outline-none"
            onClick={() => {
              setSelectedDay(day.id)
              setCurrentPage(0)
            }}
            title={day.label}
            aria-label={day.label}
          >
            <img
              src={
                selectedDay === day.id
                  ? animeSchedule.redBlock
                  : animeSchedule.greyBlock
              }
              alt={day.label}
              loading="lazy"
            />
            <span className="absolute inset-0 flex items-center justify-center font-semibold pointer-events-none">
              {day.label}
            </span>
          </button>
        ))}
      </div>

      {/* Loader */}
      {loading.schedule && !currentItems.length && (
        <div className="text-center mt-4" role="status">
          טוען נתונים…
        </div>
      )}

      {/* גריד אנימות */}
      <div className="mt-4 gap-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {paginatedItems.map((item) => {
          const inWatchIcon = isInWatchIcon(item.mal_id)

          return (
            <div key={item.mal_id} className="flex flex-col items-center group">
              <Link
                to={getAnimeDetailsUrl(item)}
                className="w-[70%] self-start"
                title={`פרטים על ${item.title_english || item.title}`}
              >
                <div className="w-[170px] h-[220px] md:w-[180px] xl:w-[240px] xl:h-[300px] overflow-hidden">
                  <img
                    src={item.image_url}
                    alt={`פוסטר ${item.title_english || item.title}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </Link>

              <div className="flex justify-between items-center mt-2 w-[170px] md:w-[180px] xl:w-[240px]">
                <Link
                  to={getAnimeDetailsUrl(item)}
                  className="w-[70%] self-start"
                  title={`דף אנימה: ${item.title_english || item.title}`}
                >
                  <span className="group-hover:text-red-500 transition-colors duration-300">
                    {item.title_hebrew} //{' '}
                  </span>
                  <span className="group-hover:text-red-500 transition-colors duration-300">
                    {item.title_english || item.title}
                  </span>
                </Link>

                <button
                  type="button"
                  onClick={() => handleToggleWatch(item)}
                  className="self-start"
                  title={
                    inWatchIcon ? 'הסר מרשימת הצפייה' : 'הוסף לרשימת הצפייה'
                  }
                  aria-label={
                    inWatchIcon ? 'הסר מרשימת הצפייה' : 'הוסף לרשימת הצפייה'
                  }
                >
                  <WatchlistIcon
                    className={clsx(
                      'transition-transform duration-300 hover:fill-primary',
                      inWatchIcon ? 'fill-primary' : 'fill-[#363636]'
                    )}
                  />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* עמוס עמודים */}
      <div
  className="flex justify-center mt-4 gap-2"
  role="navigation"
  aria-label="דפדוף עמודים"
>
  {Array.from({
    length: Math.ceil(currentItems.length / itemsPerPage),
  }).map((_, idx) => (
<button
  key={idx}
  type="button"
  onClick={() => setCurrentPage(idx)}
  title={`עמוד ${idx + 1}`}
  aria-label={`עמוד ${idx + 1}`}
  aria-current={idx === currentPage ? 'page' : undefined}
  className="group"
>
  <span className="sr-only">
    {idx === currentPage
      ? `עמוד ${idx + 1} נבחר`
      : `מעבר לעמוד ${idx + 1}`}
  </span>
  <FireDot
    fillColor={
      idx === currentPage ? '#bd0029' : '#9E9D92'
    }
    className="w-4 h-4 group-hover:scale-110 transition-transform duration-200"
  />
</button>

  ))}
</div>


      {/* CTA לאורח */}
      {!loggedInUser && (
        <div className="flex flex-col items-end mt-4">
          <div className="flex gap-8">
            <Link
              state={{ backgroundLocation: location }}
              to="/signup"
              className="flex items-center gap-2 hover:text-primary"
              title="צור חשבון חדש"
            >
              <img src={images.unloggedIcon} alt="" className="w-7 h-7" />
              <span>יצירת&nbsp;משתמש</span>
            </Link>

            <button
              type="button"
              className="flex items-center gap-2"
              title="הירשם לניוזלטר"
              aria-label="הירשם לניוזלטר"
            >
              <img src={animeSchedule.mailIcon} alt="" className="w-7 h-7" />
              <span className="hover:text-primary">הרשמה&nbsp;לעדכונים</span>
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
